import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import mcqBank from '../../data/questions/mcq-bank.json';
import gcsePsychologyBank from '../../data/questions/gcse/psychology-mcq-bank.json';
import gcseBiologyBank from '../../data/questions/gcse/biology/biology-mcqs.json';
import gcseChemistryBank from '../../data/questions/gcse/chemistry/chemistry-mcqs.json';
import gcsePhysicsBank from '../../data/questions/gcse/physics/physics-mcqs.json';
import envAtmosphereBank from '../../data/questions/a-level/env-science/mcq-bank/atmosphere-mcqs.json';
import envHydrosphereBank from '../../data/questions/a-level/env-science/mcq-bank/hydrosphere-mcqs.json';
import envLithosphereBank from '../../data/questions/a-level/env-science/mcq-bank/lithosphere-mcqs.json';
import envBiogeochemBank from '../../data/questions/a-level/env-science/mcq-bank/biogeochemical-cycles-mcqs.json';
import envSoilBank from '../../data/questions/a-level/env-science/mcq-bank/soil-mcqs.json';
import envEnergyBank from '../../data/questions/a-level/env-science/mcq-bank/energy-resources-mcqs.json';
import envPollutionBank from '../../data/questions/a-level/env-science/mcq-bank/pollution-mcqs.json';
import envResearchMethodsBank from '../../data/questions/a-level/env-science/mcq-bank/research-methods-mcqs.json';
import envConditionsForLifeBank from '../../data/questions/a-level/env-science/mcq-bank/conditions-for-life-mcqs.json';
import envConservationBank from '../../data/questions/a-level/env-science/mcq-bank/conservation-of-biodiversity-mcqs.json';
import envLifeProcessesBank from '../../data/questions/a-level/env-science/mcq-bank/life-processes-biosphere-mcqs.json';
import envAgricultureBank from '../../data/questions/a-level/env-science/mcq-bank/agriculture-mcqs.json';
import envAquaticFoodBank from '../../data/questions/a-level/env-science/mcq-bank/aquatic-food-resources-mcqs.json';
import envForestResourcesBank from '../../data/questions/a-level/env-science/mcq-bank/forest-resources-mcqs.json';
import envSustainabilityBank from '../../data/questions/a-level/env-science/mcq-bank/sustainability-mcqs.json';

// ─── Bank loading ─────────────────────────────────────────────────────────────

const ALEVEL_SPEC_REF_TO_TOPIC = {
  '3.1.1': 'water_carbon',
  '3.1.2': 'deserts',
  '3.1.5': 'hazards',
  '3.2.1': 'global_systems',
  '3.2.2': 'changing_places',
  '3.2.5': 'resource_security',
};

const GCSE_SPEC_REF_TO_TOPIC = {
  'gcse-psy:memory':           'memory',
  'gcse-psy:research-methods': 'research_methods',
  'gcse-psy:development':      'development',
  'gcse-psy:perception':       'perception',
  'gcse-psy:social-influence': 'social_influence',
  'gcse-psy:attachment':       'attachment',
  'gcse-psy:psychopathology':  'psychopathology',
};

// Maps the 2-part AQA spec prefix (e.g. "4.1") to a stable topic key
const GCSE_BIO_SPEC_PREFIX_TO_TOPIC = {
  '4.1': 'cell_biology',
  '4.2': 'organisation',
  '4.3': 'infection_response',
  '4.4': 'bioenergetics',
  '4.5': 'homeostasis_response',
  '4.6': 'inheritance_variation',
  '4.7': 'ecology',
};

const GCSE_CHEM_SPEC_PREFIX_TO_TOPIC = {
  '5.1':  'atomic_structure',
  '5.2':  'bonding_structure',
  '5.3':  'quantitative_chemistry',
  '5.4':  'chemical_changes',
  '5.5':  'energy_changes',
  '5.6':  'rate_equilibrium',
  '5.7':  'organic_chemistry',
  '5.8':  'chemical_analysis',
  '5.9':  'atmosphere',
  '5.10': 'using_resources',
};

const GCSE_PHYS_SPEC_PREFIX_TO_TOPIC = {
  '6.1': 'energy',
  '6.2': 'electricity',
  '6.3': 'particle_model',
  '6.4': 'atomic_structure_phys',
  '6.5': 'forces',
  '6.6': 'waves',
  '6.7': 'magnetism',
};

// Normalise each bank entry into a shape the quiz can use directly.
// Legacy MCQs carry their composite key fields in legacy_metadata.
// Pipeline C MCQs are given placeholder composite key values so they can
// be stored in mcq_question_stats under the existing unique constraint:
//   (user_id, year=0, paper=0, topic, question_id=content_hash, question_index=1)
function loadBank(bank, specRefMap) {
  return bank.map((q) => {
    if (q.legacy_metadata) {
      const lm = q.legacy_metadata;
      return {
        ...q,
        _topic:         lm.topic,
        _year:          lm.year,
        _paper:         lm.paper,
        _questionId:    lm.question_id,
        _questionIndex: lm.question_index,
        _displayLabel:  lm.display_label ?? null,
        _compositeKey:  `${lm.year}::${lm.paper}::${lm.topic}::${lm.question_id}::${lm.question_index}`,
      };
    }
    // Pipeline C
    const pm = q.pipeline_metadata;
    const topic = specRefMap[pm.spec_ref] ?? pm.spec_ref;
    return {
      ...q,
      _topic:         topic,
      _year:          0,
      _paper:         0,
      _questionId:    pm.content_hash,
      _questionIndex: 1,
      _displayLabel:  null,
      _compositeKey:  `0::0::${topic}::${pm.content_hash}::1`,
    };
  });
}

// Loader for the new science MCQ format: spec_refs[] array, content_hash at top level
function loadScienceBank(bank, specPrefixMap) {
  return bank.filter((q) => q.status === 'active').map((q) => {
    const ref    = q.spec_refs?.[0] ?? '';
    const prefix = ref.split('.').slice(0, 2).join('.');
    const topic  = specPrefixMap[prefix] ?? prefix;
    return {
      ...q,
      _topic:         topic,
      _year:          0,
      _paper:         0,
      _questionId:    q.content_hash,
      _questionIndex: 1,
      _displayLabel:  null,
      _compositeKey:  `0::0::${topic}::${q.content_hash}::1`,
    };
  });
}

// Loader for Environmental Science MCQs: uses 3-part spec prefix (e.g. "3.2.1" → "atmosphere")
function loadEnvScienceBank(bank, specPrefixMap) {
  return bank.map((q) => {
    const ref    = q.spec_refs?.[0] ?? '';
    const prefix = ref.split('.').slice(0, 3).join('.');
    const topic  = specPrefixMap[prefix] ?? prefix;
    return {
      ...q,
      _topic:         topic,
      _year:          0,
      _paper:         0,
      _questionId:    q.content_hash,
      _questionIndex: 1,
      _displayLabel:  null,
      _compositeKey:  `0::0::${topic}::${q.content_hash}::1`,
    };
  });
}

const ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC = {
  // 3.1 Living environment
  '3.1.1': 'conditions_for_life',
  '3.1.2': 'conservation_of_biodiversity',
  '3.1.3': 'life_processes_biosphere',
  // 3.2 Physical environment
  '3.2.1': 'atmosphere',
  '3.2.2': 'hydrosphere',
  '3.2.3': 'lithosphere',
  '3.2.4': 'biogeochemical_cycles',
  '3.2.5': 'soils',
  // 3.5 Biological resources
  '3.5.1': 'agriculture',
  '3.5.2': 'aquatic_food_resources',
  '3.5.3': 'forest_resources',
};

// Sections where all sub-topics are grouped into one topic (2-part prefix).
const ENV_SCIENCE_SPEC2_PREFIX_TO_TOPIC = {
  '3.3': 'energy_resources',
  '3.4': 'pollution',
  '3.6': 'sustainability',
  '3.7': 'research_methods',
};

const aLevelMcqData  = { questions: loadBank(mcqBank, ALEVEL_SPEC_REF_TO_TOPIC) };
const gcsePsychData  = { questions: loadBank(gcsePsychologyBank, GCSE_SPEC_REF_TO_TOPIC) };
const gcseBioData    = { questions: loadScienceBank(gcseBiologyBank,   GCSE_BIO_SPEC_PREFIX_TO_TOPIC) };
const gcseChemData   = { questions: loadScienceBank(gcseChemistryBank, GCSE_CHEM_SPEC_PREFIX_TO_TOPIC) };
const gcsePhysData   = { questions: loadScienceBank(gcsePhysicsBank,   GCSE_PHYS_SPEC_PREFIX_TO_TOPIC) };
const envScienceData = {
  questions: [
    // 3.1 Living environment (Paper 2)
    ...loadEnvScienceBank(envConditionsForLifeBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envConservationBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envLifeProcessesBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    // 3.2 Physical environment (Paper 1)
    ...loadEnvScienceBank(envAtmosphereBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envHydrosphereBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envLithosphereBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envBiogeochemBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envSoilBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    // 3.3 Energy resources, 3.4 Pollution (Paper 1)
    ...loadScienceBank(envEnergyBank, ENV_SCIENCE_SPEC2_PREFIX_TO_TOPIC),
    ...loadScienceBank(envPollutionBank, ENV_SCIENCE_SPEC2_PREFIX_TO_TOPIC),
    // 3.5 Biological resources (Paper 2)
    ...loadEnvScienceBank(envAgricultureBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envAquaticFoodBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    ...loadEnvScienceBank(envForestResourcesBank, ENV_SCIENCE_SPEC3_PREFIX_TO_TOPIC),
    // 3.6 Sustainability (Paper 2), 3.7 Research Methods (both papers)
    ...loadScienceBank(envSustainabilityBank, ENV_SCIENCE_SPEC2_PREFIX_TO_TOPIC),
    ...loadScienceBank(envResearchMethodsBank, ENV_SCIENCE_SPEC2_PREFIX_TO_TOPIC),
  ],
};

const API_BASE = process.env.REACT_APP_API_URL;

// ─── Constants ────────────────────────────────────────────────────────────────

const QUESTIONS_PER_QUIZ = 10;

const ALEVEL_TOPIC_DISPLAY = {
  water_carbon:      'Water & Carbon Cycles',
  hazards:           'Hazards',
  deserts:           'Hot Desert Environments',
  changing_places:   'Changing Places',
  global_systems:    'Global Systems & Governance',
  resource_security: 'Resource Security',
};

const ALEVEL_TOPIC_COLORS = {
  water_carbon:      { text: 'text-blue-400',   border: 'border-blue-400/40',   bg: 'bg-blue-400/10'   },
  hazards:           { text: 'text-red-400',    border: 'border-red-400/40',    bg: 'bg-red-400/10'    },
  deserts:           { text: 'text-yellow-400', border: 'border-yellow-400/40', bg: 'bg-yellow-400/10' },
  changing_places:   { text: 'text-orange-400', border: 'border-orange-400/40', bg: 'bg-orange-400/10' },
  global_systems:    { text: 'text-purple-400', border: 'border-purple-400/40', bg: 'bg-purple-400/10' },
  resource_security: { text: 'text-green-400',  border: 'border-green-400/40',  bg: 'bg-green-400/10'  },
};

const GCSE_PSYCH_TOPIC_DISPLAY = {
  memory:           'Memory',
  social_influence: 'Social Influence',
  attachment:       'Attachment',
  psychopathology:  'Psychopathology',
  development:      'Development',
  perception:       'Perception',
  research_methods: 'Research Methods',
};

const GCSE_PSYCH_TOPIC_COLORS = {
  memory:           { text: 'text-pink-400',   border: 'border-pink-400/40',   bg: 'bg-pink-400/10'   },
  social_influence: { text: 'text-purple-400', border: 'border-purple-400/40', bg: 'bg-purple-400/10' },
  attachment:       { text: 'text-rose-400',   border: 'border-rose-400/40',   bg: 'bg-rose-400/10'   },
  psychopathology:  { text: 'text-orange-400', border: 'border-orange-400/40', bg: 'bg-orange-400/10' },
  development:      { text: 'text-amber-400',  border: 'border-amber-400/40',  bg: 'bg-amber-400/10'  },
  perception:       { text: 'text-sky-400',    border: 'border-sky-400/40',    bg: 'bg-sky-400/10'    },
  research_methods: { text: 'text-teal-400',   border: 'border-teal-400/40',   bg: 'bg-teal-400/10'   },
};

const GCSE_BIO_TOPIC_DISPLAY = {
  cell_biology:         'B1 Cell Biology',
  organisation:         'B2 Organisation',
  infection_response:   'B3 Infection & Response',
  bioenergetics:        'B4 Bioenergetics',
  homeostasis_response: 'B5 Homeostasis & Response',
  inheritance_variation:'B6 Inheritance, Variation & Evolution',
  ecology:              'B7 Ecology',
};

const GCSE_BIO_TOPIC_COLORS = {
  cell_biology:          { text: 'text-green-400',  border: 'border-green-400/40',  bg: 'bg-green-400/10'  },
  organisation:          { text: 'text-emerald-400',border: 'border-emerald-400/40',bg: 'bg-emerald-400/10'},
  infection_response:    { text: 'text-red-400',    border: 'border-red-400/40',    bg: 'bg-red-400/10'    },
  bioenergetics:         { text: 'text-lime-400',   border: 'border-lime-400/40',   bg: 'bg-lime-400/10'   },
  homeostasis_response:  { text: 'text-teal-400',   border: 'border-teal-400/40',   bg: 'bg-teal-400/10'   },
  inheritance_variation: { text: 'text-cyan-400',   border: 'border-cyan-400/40',   bg: 'bg-cyan-400/10'   },
  ecology:               { text: 'text-sky-400',    border: 'border-sky-400/40',    bg: 'bg-sky-400/10'    },
};

const GCSE_CHEM_TOPIC_DISPLAY = {
  atomic_structure:      'C1 Atomic Structure & the Periodic Table',
  bonding_structure:     'C2 Bonding, Structure & Properties',
  quantitative_chemistry:'C3 Quantitative Chemistry',
  chemical_changes:      'C4 Chemical Changes',
  energy_changes:        'C5 Energy Changes',
  rate_equilibrium:      'C6 Rate & Extent of Chemical Change',
  organic_chemistry:     'C7 Organic Chemistry',
  chemical_analysis:     'C8 Chemical Analysis',
  atmosphere:            'C9 Chemistry of the Atmosphere',
  using_resources:       'C10 Using Resources',
};

const GCSE_CHEM_TOPIC_COLORS = {
  atomic_structure:       { text: 'text-teal-400',   border: 'border-teal-400/40',   bg: 'bg-teal-400/10'   },
  bonding_structure:      { text: 'text-cyan-400',   border: 'border-cyan-400/40',   bg: 'bg-cyan-400/10'   },
  quantitative_chemistry: { text: 'text-sky-400',    border: 'border-sky-400/40',    bg: 'bg-sky-400/10'    },
  chemical_changes:       { text: 'text-blue-400',   border: 'border-blue-400/40',   bg: 'bg-blue-400/10'   },
  energy_changes:         { text: 'text-indigo-400', border: 'border-indigo-400/40', bg: 'bg-indigo-400/10' },
  rate_equilibrium:       { text: 'text-violet-400', border: 'border-violet-400/40', bg: 'bg-violet-400/10' },
  organic_chemistry:      { text: 'text-purple-400', border: 'border-purple-400/40', bg: 'bg-purple-400/10' },
  chemical_analysis:      { text: 'text-fuchsia-400',border: 'border-fuchsia-400/40',bg: 'bg-fuchsia-400/10'},
  atmosphere:             { text: 'text-slate-400',  border: 'border-slate-400/40',  bg: 'bg-slate-400/10'  },
  using_resources:        { text: 'text-emerald-400',border: 'border-emerald-400/40',bg: 'bg-emerald-400/10'},
};

const GCSE_PHYS_TOPIC_DISPLAY = {
  energy:               'P1 Energy',
  electricity:          'P2 Electricity',
  particle_model:       'P3 Particle Model of Matter',
  atomic_structure_phys:'P4 Atomic Structure',
  forces:               'P5 Forces',
  waves:                'P6 Waves',
  magnetism:            'P7 Magnetism & Electromagnetism',
};

const GCSE_PHYS_TOPIC_COLORS = {
  energy:                { text: 'text-amber-400',  border: 'border-amber-400/40',  bg: 'bg-amber-400/10'  },
  electricity:           { text: 'text-yellow-400', border: 'border-yellow-400/40', bg: 'bg-yellow-400/10' },
  particle_model:        { text: 'text-orange-400', border: 'border-orange-400/40', bg: 'bg-orange-400/10' },
  atomic_structure_phys: { text: 'text-red-400',    border: 'border-red-400/40',    bg: 'bg-red-400/10'    },
  forces:                { text: 'text-rose-400',   border: 'border-rose-400/40',   bg: 'bg-rose-400/10'   },
  waves:                 { text: 'text-pink-400',   border: 'border-pink-400/40',   bg: 'bg-pink-400/10'   },
  magnetism:             { text: 'text-fuchsia-400',border: 'border-fuchsia-400/40',bg: 'bg-fuchsia-400/10'},
};

const ENV_SCIENCE_TOPIC_DISPLAY = {
  // 3.1 Living environment
  conditions_for_life:          'Conditions for Life on Earth',
  conservation_of_biodiversity: 'Conservation of Biodiversity',
  life_processes_biosphere:     'Life Processes & Conservation Planning',
  // 3.2 Physical environment
  atmosphere:                   'The Atmosphere',
  hydrosphere:                  'The Hydrosphere',
  lithosphere:                  'Mineral Resources (Lithosphere)',
  biogeochemical_cycles:        'Biogeochemical Cycles',
  soils:                        'Soils',
  // 3.3 & 3.4
  energy_resources:             'Energy Resources',
  pollution:                    'Pollution',
  // 3.5 Biological resources
  agriculture:                  'Agriculture',
  aquatic_food_resources:       'Aquatic Food Production',
  forest_resources:             'Forest Resources',
  // 3.6 & 3.7
  sustainability:               'Sustainability',
  research_methods:             'Research Methods',
};

const ENV_SCIENCE_TOPIC_COLORS = {
  // 3.1 — emerald/teal greens
  conditions_for_life:          { text: 'text-emerald-400', border: 'border-emerald-400/40', bg: 'bg-emerald-400/10' },
  conservation_of_biodiversity: { text: 'text-teal-400',    border: 'border-teal-400/40',    bg: 'bg-teal-400/10'   },
  life_processes_biosphere:     { text: 'text-green-400',   border: 'border-green-400/40',   bg: 'bg-green-400/10'  },
  // 3.2 — sky/earth tones
  atmosphere:                   { text: 'text-sky-400',     border: 'border-sky-400/40',     bg: 'bg-sky-400/10'    },
  hydrosphere:                  { text: 'text-blue-400',    border: 'border-blue-400/40',    bg: 'bg-blue-400/10'   },
  lithosphere:                  { text: 'text-amber-400',   border: 'border-amber-400/40',   bg: 'bg-amber-400/10'  },
  biogeochemical_cycles:        { text: 'text-lime-400',    border: 'border-lime-400/40',    bg: 'bg-lime-400/10'   },
  soils:                        { text: 'text-orange-400',  border: 'border-orange-400/40',  bg: 'bg-orange-400/10' },
  // 3.3 & 3.4
  energy_resources:             { text: 'text-yellow-400',  border: 'border-yellow-400/40',  bg: 'bg-yellow-400/10' },
  pollution:                    { text: 'text-red-400',     border: 'border-red-400/40',     bg: 'bg-red-400/10'    },
  // 3.5 — warm greens
  agriculture:                  { text: 'text-lime-400',    border: 'border-lime-400/40',    bg: 'bg-lime-400/10'   },
  aquatic_food_resources:       { text: 'text-cyan-400',    border: 'border-cyan-400/40',    bg: 'bg-cyan-400/10'   },
  forest_resources:             { text: 'text-green-400',   border: 'border-green-400/40',   bg: 'bg-green-400/10'  },
  // 3.6 & 3.7
  sustainability:               { text: 'text-purple-400',  border: 'border-purple-400/40',  bg: 'bg-purple-400/10' },
  research_methods:             { text: 'text-slate-400',   border: 'border-slate-400/40',   bg: 'bg-slate-400/10'  },
};

// AQA A-Level Geography paper splits.
// Paper 1: Physical Geography (Water & Carbon, Hot Deserts, Hazards)
// Paper 2: Human Geography (Global Systems, Changing Places, Resource Security)
const ALEVEL_GEOG_PAPER_TOPICS = {
  1: ['water_carbon', 'deserts', 'hazards'],
  2: ['global_systems', 'changing_places', 'resource_security'],
};

// AQA A-Level Environmental Science paper splits.
// Paper 1 covers spec sections 2 (Physical Environment), 3 (Energy), 4 (Pollution), 7 (Research Methods).
// Paper 2 covers spec sections 1 (Living Environment), 5 (Biological Resources), 6 (Sustainability), 7 (Research Methods).
// Research Methods (section 7) appears in both papers.
const ALEVEL_ENV_SCIENCE_PAPER_TOPICS = {
  1: ['atmosphere', 'hydrosphere', 'lithosphere', 'biogeochemical_cycles', 'soils', 'energy_resources', 'pollution', 'research_methods'],
  2: ['conditions_for_life', 'conservation_of_biodiversity', 'life_processes_biosphere', 'agriculture', 'aquatic_food_resources', 'forest_resources', 'sustainability', 'research_methods'],
};

// AQA GCSE paper splits — only defined for subjects with a clean 2-paper structure.
// Paper 1 / Paper 2 random-mix options are only shown for subjects listed here.
const GCSE_PAPER_TOPICS = {
  biology: {
    1: ['cell_biology', 'organisation', 'infection_response', 'bioenergetics'],
    2: ['homeostasis_response', 'inheritance_variation', 'ecology'],
  },
  chemistry: {
    1: ['atomic_structure', 'bonding_structure', 'quantitative_chemistry', 'chemical_changes', 'energy_changes'],
    2: ['rate_equilibrium', 'organic_chemistry', 'chemical_analysis', 'atmosphere', 'using_resources'],
  },
  physics: {
    1: ['energy', 'electricity', 'particle_model', 'atomic_structure_phys'],
    2: ['forces', 'waves', 'magnetism'],
  },
};

const RANDOM_MIX_KEYS = new Set(['random_mix', 'random_mix_paper_1', 'random_mix_paper_2']);

function paperKeyToNumber(topicKey) {
  if (topicKey === 'random_mix_paper_1') return 1;
  if (topicKey === 'random_mix_paper_2') return 2;
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Unique string key matching the DB unique constraint.
// History records returned by GET /api/quiz/history use the same field names
// (year, paper, topic, questionId, questionIndex) so the fingerprints align.
function makeFingerprint(q) {
  return q._compositeKey;
}

// Select display options from a unified-format MCQ (options[] with is_correct flag).
// Always returns exactly (correct count) + 2 randomly-chosen distractors, shuffled.
// This fixes the frontend/backend divergence: both surfaces now apply the same rule.
// MCQs with >2 distractors (e.g. Gemini_026, Gemini_058 with 4) show a different
// distractor pair on each quiz attempt.
function selectDisplayOptions(mcq) {
  const correct    = mcq.options.filter((o) => o.is_correct);
  const distractors = mcq.options.filter((o) => !o.is_correct);
  const selected   = shuffle(distractors).slice(0, 2);
  return shuffle([...correct, ...selected]);
}

// Pure selection function — history is pre-fetched by handleStart
function buildQuiz(topicKey, history = { avgTimesTaken: 0, questions: [] }, mcqData = aLevelMcqData, caseStudyOnly = false, paperTopics = null) {
  let pool;
  if (topicKey === 'random_mix') {
    pool = mcqData.questions;
  } else if (topicKey === 'random_mix_paper_1' || topicKey === 'random_mix_paper_2') {
    const paperNum = paperKeyToNumber(topicKey);
    const allowed = new Set(paperTopics?.[paperNum] ?? []);
    pool = mcqData.questions.filter((q) => allowed.has(q._topic));
  } else {
    pool = mcqData.questions.filter((q) => q._topic === topicKey);
  }

  if (caseStudyOnly) {
    pool = pool.filter((q) => q.is_case_study === true);
  }

  // Build lookup: _compositeKey → { timesTaken, timesWrong }
  // History rows use the same composite key field names returned by /api/quiz/history.
  const historyMap = new Map(
    (history.questions ?? []).map((h) => [
      `${h.year}::${h.paper}::${h.topic}::${h.questionId}::${h.questionIndex}`,
      h,
    ])
  );

  const bucketUnseen       = [];
  const bucketNeverCorrect = [];
  const bucketWrong        = [];
  // bucketCycle holds all questions answered correctly at least once, for use
  // when the higher-priority buckets are exhausted.
  const bucketCycle        = [];

  for (const q of pool) {
    const stat = historyMap.get(makeFingerprint(q));
    if (!stat || stat.timesTaken === 0) {
      bucketUnseen.push(q);
    } else if (stat.timesWrong >= stat.timesTaken) {
      bucketNeverCorrect.push({ q, timesWrong: stat.timesWrong });
    } else {
      const timesCorrect = stat.timesTaken - stat.timesWrong;
      if (stat.timesWrong > 0) {
        bucketWrong.push({ q, timesWrong: stat.timesWrong });
      }
      // All questions answered correctly at least once go into the cycle pool.
      bucketCycle.push({ q, timesCorrect, timesWrong: stat.timesWrong });
    }
  }

  console.log('[buildQuiz]', topicKey, {
    pool: pool.length,
    historyQuestions: (history.questions ?? []).length,
    historyMapSize: historyMap.size,
    bucketUnseen: bucketUnseen.length,
    bucketNeverCorrect: bucketNeverCorrect.length,
    bucketWrong: bucketWrong.length,
    bucketCycle: bucketCycle.length,
  });

  let selected = [];
  const shuffledUnseen = shuffle(bucketUnseen);
  selected.push(...shuffledUnseen.slice(0, QUESTIONS_PER_QUIZ));

  if (selected.length < QUESTIONS_PER_QUIZ) {
    const sortedNeverCorrect = bucketNeverCorrect
      .sort((a, b) => b.timesWrong - a.timesWrong)
      .map(({ q }) => q);
    selected.push(...sortedNeverCorrect.slice(0, QUESTIONS_PER_QUIZ - selected.length));
  }

  if (selected.length < QUESTIONS_PER_QUIZ) {
    const sortedWrong = bucketWrong
      .sort((a, b) => b.timesWrong - a.timesWrong)
      .map(({ q }) => q);
    selected.push(...sortedWrong.slice(0, QUESTIONS_PER_QUIZ - selected.length));
  }

  if (selected.length < QUESTIONS_PER_QUIZ) {
    // Cycle through questions answered correctly at least once.
    // Primary: fewest correct answers first. Secondary: most wrong answers first
    // (shakiest knowledge within the same correct-answer tier).
    const selectedKeys = new Set(selected.map((q) => makeFingerprint(q)));
    const sortedCycle = bucketCycle
      .filter(({ q }) => !selectedKeys.has(makeFingerprint(q)))
      .sort((a, b) => a.timesCorrect - b.timesCorrect || b.timesWrong - a.timesWrong)
      .map(({ q }) => q);
    selected.push(...sortedCycle.slice(0, QUESTIONS_PER_QUIZ - selected.length));
  }

  return shuffle(selected).slice(0, QUESTIONS_PER_QUIZ).map((q) => {
    const displayOptions   = selectDisplayOptions(q);
    const correct_answer   = displayOptions.find((o) => o.is_correct).text;
    const shuffledAnswers  = displayOptions.map((o) => o.text);
    return { ...q, correct_answer, shuffledAnswers };
  });
}

function getAvailableTopics(mcqData, topicDisplay) {
  const available = new Set(mcqData.questions.map((q) => q._topic));
  // Use the key order from topicDisplay (defined in spec order) rather than sorting alphabetically
  return Object.keys(topicDisplay).filter((t) => available.has(t));
}

// ─── Setup Screen ─────────────────────────────────────────────────────────────

function SetupScreen({ onStart, masteredCounts, mcqData, topicDisplay, topicColors, caseStudyOnly, onToggleCaseStudy, paperTopics }) {
  const [selected, setSelected] = useState(null);
  const topics = useMemo(() => getAvailableTopics(mcqData, topicDisplay), [mcqData, topicDisplay]);
  const hasCaseStudies = useMemo(() => mcqData.questions.some((q) => q.is_case_study === true), [mcqData]);

  const paperPools = useMemo(() => {
    if (!paperTopics) return null;
    const pool = (paperNum) => {
      const allowed = new Set(paperTopics[paperNum] ?? []);
      return mcqData.questions.filter((q) => allowed.has(q._topic));
    };
    return { 1: pool(1), 2: pool(2) };
  }, [paperTopics, mcqData]);

  const selectedCsCount = useMemo(() => {
    if (!selected || RANDOM_MIX_KEYS.has(selected)) return null;
    return mcqData.questions.filter((q) => q._topic === selected && q.is_case_study === true).length;
  }, [selected, mcqData]);

  const toggleDisabled = !selected || (!RANDOM_MIX_KEYS.has(selected) && selectedCsCount === 0);

  // Auto-turn off case study filter when the selected topic has none
  useEffect(() => {
    if (caseStudyOnly && toggleDisabled) onToggleCaseStudy();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleDisabled]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full bg-[#58a6ff]" />
          <span className="text-[10px] font-mono text-[#58a6ff] uppercase tracking-widest">MCQ Quiz</span>
        </div>
        <h2 className="text-[#e6edf3] text-lg font-semibold mb-1">Choose your focus</h2>
        <p className="text-[#8b949e] text-xs">
          Select a topic for a focused session, or pick a random mix across all topics.
          You'll get {QUESTIONS_PER_QUIZ} questions with immediate feedback.
        </p>
      </div>

      {/* Options */}
      <div className="flex-1 px-6 pb-6 space-y-2 overflow-y-auto">
        {paperPools ? (
          /* GCSE sciences: Paper 1 / Paper 2 pill grid (mirrors mock-exam geography selector) */
          <div className="grid grid-cols-2 gap-2">
            {[1, 2].map((paperNum) => {
              const key = `random_mix_paper_${paperNum}`;
              const pool = paperPools[paperNum];
              const csCount = pool.filter((q) => q.is_case_study === true).length;
              const isSelected = selected === key;
              const disabled = pool.length === 0 || (caseStudyOnly && csCount === 0);
              const topicSummary = pool.length === 0
                ? 'No questions available yet'
                : (paperTopics[paperNum] ?? []).map((t) => topicDisplay[t] ?? t).join(' · ');
              return (
                <button
                  key={key}
                  onClick={() => !disabled && setSelected(key)}
                  disabled={disabled}
                  className={`rounded-xl border p-3 text-left transition-colors ${
                    disabled
                      ? 'border-[#21262d] bg-[#161b22] opacity-40 cursor-not-allowed'
                      : isSelected
                      ? 'border-[#238636] bg-[#238636]/10'
                      : 'border-[#30363d] bg-[#161b22] hover:border-[#8b949e]'
                  }`}
                >
                  <div className={`text-xs font-semibold mb-1 ${isSelected ? 'text-[#3fb950]' : 'text-[#e6edf3]'}`}>
                    Paper {paperNum}
                  </div>
                  <div className="text-[10px] text-[#8b949e] leading-relaxed">{topicSummary}</div>
                </button>
              );
            })}
          </div>
        ) : (
          /* A-Level / GCSE psychology: Random Mix across all topics */
          <button
            onClick={() => setSelected('random_mix')}
            className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border text-left transition-all ${
              selected === 'random_mix'
                ? 'border-[#58a6ff]/60 bg-[#58a6ff]/8 shadow-[0_0_0_1px_rgba(88,166,255,0.2)]'
                : 'border-[#30363d] hover:border-[#58a6ff]/30 hover:bg-[#161b22]'
            }`}
          >
            <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
              selected === 'random_mix' ? 'bg-[#58a6ff]/20' : 'bg-[#21262d]'
            }`}>
              <svg className={`w-4 h-4 ${selected === 'random_mix' ? 'text-[#58a6ff]' : 'text-[#8b949e]'}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h16M4 12h8m-8 6h16" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${selected === 'random_mix' ? 'text-[#58a6ff]' : 'text-[#e6edf3]'}`}>
                Random Mix
              </p>
              <p className="text-[#8b949e] text-[11px] mt-0.5">
                {caseStudyOnly
                  ? `${mcqData.questions.filter((q) => q.is_case_study).length} case study questions across all topics`
                  : `${masteredCounts._total ?? 0} / ${mcqData.questions.length} mastered across all topics`}
              </p>
            </div>
            {selected === 'random_mix' && (
              <svg className="w-4 h-4 text-[#58a6ff] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 py-1">
          <div className="flex-1 border-t border-[#21262d]" />
          <span className="text-[#484f58] text-[10px] font-mono uppercase tracking-wider">or pick a topic</span>
          <div className="flex-1 border-t border-[#21262d]" />
        </div>

        {/* Topic options */}
        {topics.map((topic) => {
          const allCount  = mcqData.questions.filter((q) => q._topic === topic).length;
          const csCount   = mcqData.questions.filter((q) => q._topic === topic && q.is_case_study).length;
          const count     = caseStudyOnly ? csCount : allCount;
          const colours   = topicColors[topic] ?? { text: 'text-[#8b949e]', border: 'border-[#30363d]', bg: 'bg-[#21262d]' };
          const isSelected = selected === topic;
          const disabled  = caseStudyOnly && csCount === 0;
          return (
            <button
              key={topic}
              onClick={() => !disabled && setSelected(topic)}
              disabled={disabled}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border text-left transition-all ${
                disabled
                  ? 'border-[#21262d] opacity-40 cursor-not-allowed'
                  : isSelected
                  ? `${colours.border} ${colours.bg} shadow-[0_0_0_1px_rgba(255,255,255,0.05)]`
                  : 'border-[#30363d] hover:border-[#30363d] hover:bg-[#161b22]'
              }`}
            >
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colours.text.replace('text-', 'bg-')}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isSelected ? colours.text : 'text-[#e6edf3]'}`}>
                  {topicDisplay[topic] ?? topic}
                </p>
                <p className="text-[#8b949e] text-[11px] mt-0.5">
                  {caseStudyOnly
                    ? `${csCount} case study question${csCount !== 1 ? 's' : ''}`
                    : `${masteredCounts[topic] ?? 0} / ${count} mastered`}
                </p>
              </div>
              {isSelected && (
                <svg className={`w-4 h-4 flex-shrink-0 ${colours.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {/* Start button */}
      <div className="px-6 pb-6 border-t border-[#21262d] pt-4 space-y-3">
        {hasCaseStudies && (
          <button
            type="button"
            onClick={toggleDisabled ? undefined : onToggleCaseStudy}
            disabled={toggleDisabled}
            title={toggleDisabled ? (!selected ? 'Select a paper or topic first' : 'No case study questions available for this topic') : undefined}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all ${
              toggleDisabled
                ? 'border-[#21262d] opacity-40 cursor-not-allowed'
                : 'border-[#30363d] hover:border-[#58a6ff]/30 hover:bg-[#161b22]'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <svg className="w-3.5 h-3.5 text-[#8b949e] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-xs font-mono text-[#8b949e]">Case Studies Only</span>
            </div>
            <div className={`relative rounded-full transition-colors flex-shrink-0 ${caseStudyOnly ? 'bg-[#58a6ff]' : 'bg-black'}`}
              style={{ height: '18px', width: '32px' }}>
              <span className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white transition-all ${caseStudyOnly ? 'left-[16px]' : 'left-[2px]'}`} />
            </div>
          </button>
        )}
        <button
          onClick={() => selected && onStart(selected)}
          disabled={!selected}
          className={`w-full py-3 rounded-lg text-sm font-semibold transition-all ${
            selected
              ? 'bg-[#238636] hover:bg-[#2ea043] text-white border border-[#238636] hover:border-[#2ea043]'
              : 'bg-[#21262d] text-[#484f58] border border-[#30363d] cursor-not-allowed'
          }`}
        >
          Start {QUESTIONS_PER_QUIZ}-Question Quiz
        </button>
      </div>
    </div>
  );
}

// ─── Answer Option ─────────────────────────────────────────────────────────────

const AnswerOption = React.forwardRef(function AnswerOption({ text, state, onClick, shortcut }, ref) {
  const styles = {
    idle:    'border-[#30363d] text-[#e6edf3] hover:border-[#58a6ff]/40 hover:bg-[#58a6ff]/5 cursor-pointer',
    correct: 'border-[#3fb950] bg-[#3fb950]/10 text-[#3fb950] cursor-default',
    wrong:   'border-[#f85149] bg-[#f85149]/10 text-[#f85149] line-through cursor-default',
    dimmed:  'border-[#21262d] text-[#484f58] cursor-default',
  };

  return (
    <button
      ref={ref}
      onClick={state === 'idle' ? onClick : undefined}
      className={`w-full text-left px-4 py-3 rounded-lg border text-sm leading-snug transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#58a6ff]/60 ${styles[state] ?? styles.idle}`}
    >
      <div className="flex items-start gap-3">
        {shortcut !== undefined ? (
          <span
            aria-hidden="true"
            className={`flex-shrink-0 w-5 h-5 rounded border text-[10px] font-mono font-semibold flex items-center justify-center mt-0.5 ${
              state === 'correct' ? 'border-[#3fb950]/60 text-[#3fb950]' :
              state === 'wrong'   ? 'border-[#f85149]/60 text-[#f85149]' :
              state === 'dimmed'  ? 'border-[#21262d] text-[#484f58]' :
              'border-[#30363d] text-[#8b949e]'
            }`}
          >
            {shortcut}
          </span>
        ) : (
          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
            state === 'correct' ? 'bg-[#3fb950]' :
            state === 'wrong'   ? 'bg-[#f85149]' : 'bg-[#484f58]'
          }`} />
        )}
        {text}
      </div>
    </button>
  );
});

// ─── Question Screen ───────────────────────────────────────────────────────────

function QuestionScreen({ question, questionNumber, totalQuestions, onAnswer, onNext, answered }) {
  const firstAnswerRef = useRef(null);
  const nextButtonRef  = useRef(null);

  useEffect(() => {
    firstAnswerRef.current?.focus();
  }, [questionNumber]);

  useEffect(() => {
    if (answered) nextButtonRef.current?.focus();
  }, [answered]);

  const handleSelect = useCallback((answer) => {
    if (!answered) onAnswer(answer);
  }, [answered, onAnswer]);

  // Keyboard shortcuts: 1/2/3 select the corresponding answer.
  // Only active before the user has answered. Skips when the user is typing
  // in an input/textarea or holding a modifier key.
  useEffect(() => {
    if (answered) return undefined;
    const handler = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      const index = ['1', '2', '3'].indexOf(e.key);
      if (index === -1) return;
      const choice = question.shuffledAnswers?.[index];
      if (choice == null) return;
      e.preventDefault();
      handleSelect(choice);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [answered, question.shuffledAnswers, handleSelect]);

  const isCorrect = answered && answered === question.correct_answer;

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="px-6 pt-5 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[#8b949e] text-[11px] font-mono">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span className="text-[#484f58] text-[11px] font-mono">
            {Math.round(((questionNumber - 1) / totalQuestions) * 100)}% complete
          </span>
        </div>
        <div className="h-1 bg-[#21262d] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#58a6ff] rounded-full transition-all duration-500"
            style={{ width: `${((questionNumber - 1) / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Question text */}
      <div className="px-6 pb-4">
        <div className="bg-[#161b22] border border-[#30363d] rounded-lg px-4 py-4">
          {question._displayLabel && (
            <p className="text-[#484f58] text-[10px] font-mono mb-2 uppercase tracking-wider">
              {question._displayLabel}
            </p>
          )}
          <p className="text-[#e6edf3] text-sm leading-relaxed">{question.question_text}</p>
        </div>
      </div>

      {/* Answer options */}
      <div className="flex-1 px-6 pb-4 space-y-2 overflow-y-auto">
        {question.shuffledAnswers.map((answer, i) => {
          let state = 'idle';
          if (answered) {
            if (answer === answered) {
              state = answer === question.correct_answer ? 'correct' : 'wrong';
            } else {
              state = 'dimmed';
            }
          }
          return (
            <AnswerOption
              key={i}
              ref={i === 0 ? firstAnswerRef : null}
              text={answer}
              state={state}
              shortcut={i + 1}
              onClick={() => handleSelect(answer)}
            />
          );
        })}
      </div>

      {/* Explanation + Next */}
      {answered && (
        <div className="px-6 pb-6 space-y-3">
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
            isCorrect
              ? 'border-[#3fb950]/30 bg-[#3fb950]/8 text-[#3fb950]'
              : 'border-[#f85149]/30 bg-[#f85149]/8 text-[#f85149]'
          }`}>
            {isCorrect ? (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="text-xs font-semibold">{isCorrect ? 'Correct!' : 'Incorrect'}</span>
          </div>

          <div className="bg-[#161b22] border border-[#30363d] rounded-lg px-4 py-3">
            <p className="text-[#8b949e] text-[10px] font-mono uppercase tracking-wider mb-1.5">Examiner Explanation</p>
            <p className="text-[#e6edf3] text-xs leading-relaxed">{question.explanation}</p>
          </div>

          <button
            onClick={onNext}
            ref={nextButtonRef}
            className="w-full py-2.5 rounded-lg bg-[#21262d] hover:bg-[#30363d] border border-[#30363d] hover:border-[#8b949e] text-[#e6edf3] text-sm font-semibold transition-all flex items-center justify-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-[#58a6ff]/60"
          >
            {questionNumber === totalQuestions ? 'See Results' : 'Next Question'}
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Completion Screen ─────────────────────────────────────────────────────────

function CompletionScreen({ score, totalQuestions, topic, pointsEarned, onClose, onRetry, topicDisplay }) {
  const pct = Math.round((score / totalQuestions) * 100);
  const topicLabel =
    topic === 'random_mix'         ? 'Random Mix' :
    topic === 'random_mix_paper_1' ? 'Random Mix · Paper 1' :
    topic === 'random_mix_paper_2' ? 'Random Mix · Paper 2' :
    (topicDisplay[topic] ?? topic);

  const colour =
    pct >= 80 ? { ring: 'text-[#3fb950]', bg: 'bg-[#3fb950]/10', border: 'border-[#3fb950]/30', label: 'Excellent', text: 'text-[#3fb950]' } :
    pct >= 60 ? { ring: 'text-[#d29922]', bg: 'bg-[#d29922]/10', border: 'border-[#d29922]/30', label: 'Good',      text: 'text-[#d29922]' } :
                { ring: 'text-[#f85149]', bg: 'bg-[#f85149]/10', border: 'border-[#f85149]/30', label: 'Keep going', text: 'text-[#f85149]' };

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-8 text-center">
      <div className={`w-28 h-28 rounded-full border-4 ${colour.border} ${colour.bg} flex flex-col items-center justify-center mb-6`}>
        <span className={`text-3xl font-bold font-mono tabular-nums ${colour.ring}`}>{score}</span>
        <span className="text-[#484f58] text-xs font-mono">/{totalQuestions}</span>
      </div>

      <p className={`text-base font-semibold mb-1 ${colour.text}`}>{colour.label}</p>
      <p className="text-[#e6edf3] text-sm mb-1">
        You scored <span className="font-bold">{score} out of {totalQuestions}</span>
      </p>
      <p className="text-[#8b949e] text-xs mb-1">{pct}% correct</p>
      <div className="flex items-center gap-1.5 mt-1 mb-8">
        <span className="w-1.5 h-1.5 rounded-full bg-[#58a6ff]" />
        <span className="text-[#8b949e] text-[11px] font-mono">{topicLabel}</span>
      </div>

      {pointsEarned != null && (
        <p className="text-[#58a6ff] text-sm font-semibold mb-2">+{pointsEarned} points earned</p>
      )}
      <p className="text-[#484f58] text-[11px] mb-6">Your result has been saved to your Results Ledger.</p>

      <div className="w-full space-y-2">
        <button
          onClick={onRetry}
          className="w-full py-2.5 rounded-lg bg-[#238636] hover:bg-[#2ea043] border border-[#238636] text-white text-sm font-semibold transition-all"
        >
          Start Another Quiz
        </button>
        <button
          onClick={onClose}
          className="w-full py-2.5 rounded-lg bg-[#21262d] hover:bg-[#30363d] border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] text-sm font-semibold transition-all"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ─── Main Orchestrator ────────────────────────────────────────────────────────

export default function QuizOrchestrator({ onClose, onPointsRefresh, examLevel = 'a-level', gcseSubject = 'psychology', aLevelSubject = 'geography' }) {
  const { getAccessTokenSilently } = useAuth0();

  const isGcse = examLevel === 'gcse';

  const GCSE_DATA_MAP = {
    psychology: { data: gcsePsychData,  display: GCSE_PSYCH_TOPIC_DISPLAY, colors: GCSE_PSYCH_TOPIC_COLORS },
    biology:    { data: gcseBioData,    display: GCSE_BIO_TOPIC_DISPLAY,   colors: GCSE_BIO_TOPIC_COLORS   },
    chemistry:  { data: gcseChemData,   display: GCSE_CHEM_TOPIC_DISPLAY,  colors: GCSE_CHEM_TOPIC_COLORS  },
    physics:    { data: gcsePhysData,   display: GCSE_PHYS_TOPIC_DISPLAY,  colors: GCSE_PHYS_TOPIC_COLORS  },
  };

  const ALEVEL_DATA_MAP = {
    geography:   { data: aLevelMcqData,  display: ALEVEL_TOPIC_DISPLAY,       colors: ALEVEL_TOPIC_COLORS,       paperTopics: ALEVEL_GEOG_PAPER_TOPICS },
    'env-science': { data: envScienceData, display: ENV_SCIENCE_TOPIC_DISPLAY, colors: ENV_SCIENCE_TOPIC_COLORS, paperTopics: ALEVEL_ENV_SCIENCE_PAPER_TOPICS },
  };

  const gcseConfig   = GCSE_DATA_MAP[gcseSubject] ?? GCSE_DATA_MAP.biology;
  const aLevelConfig = ALEVEL_DATA_MAP[aLevelSubject] ?? ALEVEL_DATA_MAP.geography;
  const mcqData      = isGcse ? gcseConfig.data       : aLevelConfig.data;
  const topicDisplay = isGcse ? gcseConfig.display    : aLevelConfig.display;
  const topicColors  = isGcse ? gcseConfig.colors     : aLevelConfig.colors;
  const paperTopics  = isGcse ? (GCSE_PAPER_TOPICS[gcseSubject] ?? null) : aLevelConfig.paperTopics;

  const [phase, setPhase]         = useState('setup');
  const [topic, setTopic]         = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers]     = useState([]);
  const [saving, setSaving]       = useState(false);
  const [pointsEarned, setPointsEarned] = useState(null);
  const [masteredCounts, setMasteredCounts] = useState({});
  const [caseStudyOnly, setCaseStudyOnly] = useState(false);

  const score = answers.filter((a, i) => a === questions[i]?.correct_answer).length;

  const refreshMasteredCounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${API_BASE}/api/quiz/history?topic=random_mix`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const activeTopics = new Set(mcqData.questions.map((q) => q._topic));
      const counts = { _total: 0, _seen: 0 };
      for (const h of (data.questions ?? [])) {
        if (!activeTopics.has(h.topic)) continue;
        if (h.timesTaken > 0) counts._seen += 1;
        if (h.timesTaken > h.timesWrong) {
          counts[h.topic] = (counts[h.topic] ?? 0) + 1;
          counts._total += 1;
        }
      }
      setMasteredCounts(counts);
    } catch {
      // Non-blocking
    }
  }, [getAccessTokenSilently, mcqData]);

  useEffect(() => { refreshMasteredCounts(); }, [refreshMasteredCounts]);

  const handleStart = useCallback(async (selectedTopic) => {
    setPhase('loading');
    let history = { avgTimesTaken: 0, questions: [] };
    try {
      const token = await getAccessTokenSilently();
      // Paper-mix variants need all-topic history (each question's stats are
      // stored under its own topic key); the server only knows 'random_mix'
      // as the wildcard.
      const topicParam = RANDOM_MIX_KEYS.has(selectedTopic) ? 'random_mix' : selectedTopic;
      const res = await fetch(`${API_BASE}/api/quiz/history?topic=${topicParam}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        history = await res.json();
        console.log('[handleStart] history response:', { questionCount: history.questions?.length, avgTimesTaken: history.avgTimesTaken });
      } else {
        console.warn('[handleStart] history fetch failed:', res.status, res.statusText);
      }
    } catch (err) {
      console.error('[handleStart] history fetch error:', err);
    }
    const quiz = buildQuiz(selectedTopic, history, mcqData, caseStudyOnly, paperTopics);
    setTopic(selectedTopic);
    setQuestions(quiz);
    setAnswers([]);
    setCurrentIndex(0);
    setPhase('quiz');
  }, [getAccessTokenSilently, mcqData, caseStudyOnly, paperTopics]);

  const handleAnswer = useCallback((answer) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[currentIndex] = answer;
      return next;
    });
  }, [currentIndex]);

  const handleNext = useCallback(async () => {
    if (saving) return;
    document.activeElement?.blur();
    const nextIndex = currentIndex + 1;
    if (nextIndex < questions.length) {
      setCurrentIndex(nextIndex);
    } else {
      setSaving(true);
      try {
        const token = await getAccessTokenSilently();
        // mcq_id is sent directly from the bank entry (q.id).
        // Composite key fields are also sent for backward compatibility with the
        // stats table unique constraint and points system; they'll be removed in Phase 3.
        const questionResults = questions.map((q, i) => ({
          mcq_id:         q.id,
          year:           q._year,
          paper:          q._paper,
          topic:          q._topic,
          question_id:    q._questionId,
          question_index: q._questionIndex,
          was_correct:    answers[i] === q.correct_answer,
        }));
        const resp = await fetch(`${API_BASE}/api/quiz/submit-questions`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ topic, questions: questionResults }),
        });
        const data = await resp.json();
        if (data.pointsEarned != null) setPointsEarned(data.pointsEarned);
        onPointsRefresh?.();
      } catch {
        // Non-blocking — UI still shows completion
      } finally {
        setSaving(false);
        setPhase('complete');
      }
    }
  }, [currentIndex, questions, answers, topic, saving, getAccessTokenSilently, onPointsRefresh]);

  const handleRetry = useCallback(() => {
    setPhase('setup');
    setQuestions([]);
    setAnswers([]);
    setCurrentIndex(0);
    setTopic(null);
    setPointsEarned(null);
    refreshMasteredCounts();
  }, [refreshMasteredCounts]);

  const currentQuestion = questions[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#0d1117]/80 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-[#0d1117] border border-[#30363d] rounded-xl w-full max-w-lg shadow-2xl min-h-[500px] flex flex-col">

        <div className="flex items-center justify-between px-6 py-4 border-b border-[#30363d] flex-shrink-0">
          <div>
            <h2 className="text-[#e6edf3] text-sm font-semibold">MCQ Quiz</h2>
            <p className="text-[#8b949e] text-xs mt-0.5">
              {phase === 'setup' ? 'Configure your quiz session' :
               phase === 'quiz'  ? (() => {
                 const topicLabel =
                   topic === 'random_mix'         ? 'Random Mix' :
                   topic === 'random_mix_paper_1' ? 'Random Mix · Paper 1' :
                   topic === 'random_mix_paper_2' ? 'Random Mix · Paper 2' :
                   (topicDisplay[topic] ?? topic);
                 const csName = currentQuestion?.case_study_referenced;
                 return csName ? `${topicLabel} · ${csName}` : topicLabel;
               })() :
               'Quiz complete'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-[#e6edf3] hover:bg-[#161b22] transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {phase === 'setup' && <SetupScreen onStart={handleStart} masteredCounts={masteredCounts} mcqData={mcqData} topicDisplay={topicDisplay} topicColors={topicColors} caseStudyOnly={caseStudyOnly} onToggleCaseStudy={() => setCaseStudyOnly((v) => !v)} paperTopics={paperTopics} />}

          {phase === 'loading' && (
            <div className="flex items-center justify-center py-24">
              <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
            </div>
          )}

          {phase === 'quiz' && currentQuestion && !saving && (
            <QuestionScreen
              question={currentQuestion}
              questionNumber={currentIndex + 1}
              totalQuestions={questions.length}
              answered={answers[currentIndex] ?? null}
              onAnswer={handleAnswer}
              onNext={handleNext}
            />
          )}

          {phase === 'quiz' && saving && (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="w-5 h-5 border-2 border-[#30363d] border-t-[#58a6ff] rounded-full animate-spin" />
              <p className="text-[#8b949e] text-sm">Saving your results…</p>
            </div>
          )}

          {phase === 'complete' && (
            <CompletionScreen
              score={score}
              totalQuestions={questions.length}
              topic={topic}
              pointsEarned={pointsEarned}
              onClose={onClose}
              onRetry={handleRetry}
              topicDisplay={topicDisplay}
            />
          )}
        </div>
      </div>
    </div>
  );
}
